import type {
  Bundle,
  BundleEntry,
  CarePlan,
  Goal,
  ServiceRequest,
} from '@medplum/fhirtypes';

import { type PlanInstanceSpec, compileInstanceSpec } from '@eluve/plan-shapes';

// Accept = the in-place flip (ENG-4699): the draft CarePlan keeps its id but
// goes draft→active / proposal→plan, and its children are reconciled from the
// final tailored spec (delete the proposed ones, mint the accepted ones with
// phase choreography). One transaction, executed browser-side under the
// clinician's token. Identifier, encounter, and the match extension are
// preserved so the tab/callout still resolve the accepted plan.
export function buildAcceptBundle(params: {
  draftCarePlan: CarePlan;
  existingGoals: Goal[];
  existingServiceRequests: ServiceRequest[];
  finalSpec: PlanInstanceSpec;
  authorRef?: string;
  authorDisplay?: string;
}): Bundle {
  const {
    draftCarePlan,
    existingGoals,
    existingServiceRequests,
    finalSpec,
    authorRef,
    authorDisplay,
  } = params;

  const carePlanId = draftCarePlan.id;
  const patientRef = draftCarePlan.subject?.reference;
  if (!carePlanId || !patientRef) {
    throw new Error('Draft care plan is missing an id or patient');
  }

  const compiled = compileInstanceSpec(finalSpec, {
    patientRef,
    authorRef,
    authorDisplay,
    posture: 'accepted',
  });

  const entries: BundleEntry[] = [];

  // Drop the proposed children first; the accepted set is minted fresh.
  for (const sr of existingServiceRequests) {
    if (sr.id) {
      entries.push({
        request: { method: 'DELETE', url: `ServiceRequest/${sr.id}` },
      });
    }
  }
  for (const goal of existingGoals) {
    if (goal.id) {
      entries.push({ request: { method: 'DELETE', url: `Goal/${goal.id}` } });
    }
  }

  const goalEntries: BundleEntry[] = compiled.goals.map((goal) => ({
    fullUrl: `urn:uuid:${crypto.randomUUID()}`,
    resource: goal,
    request: { method: 'POST', url: 'Goal' },
  }));
  entries.push(...goalEntries);

  const carePlanRef = `CarePlan/${carePlanId}`;
  entries.push({
    fullUrl: carePlanRef,
    resource: {
      ...compiled.carePlan,
      id: carePlanId,
      ...(draftCarePlan.identifier
        ? { identifier: draftCarePlan.identifier }
        : {}),
      ...(draftCarePlan.encounter
        ? { encounter: draftCarePlan.encounter }
        : {}),
      ...(draftCarePlan.extension
        ? { extension: draftCarePlan.extension }
        : {}),
      ...(goalEntries.length > 0
        ? { goal: goalEntries.map((e) => ({ reference: e.fullUrl })) }
        : {}),
    },
    request: { method: 'PUT', url: carePlanRef },
  });

  for (const sr of compiled.serviceRequests) {
    entries.push({
      fullUrl: `urn:uuid:${crypto.randomUUID()}`,
      resource: { ...sr, basedOn: [{ reference: carePlanRef }] },
      request: { method: 'POST', url: 'ServiceRequest' },
    });
  }

  return { resourceType: 'Bundle', type: 'transaction', entry: entries };
}

// executeBatch can resolve with per-entry failures in the response bundle rather
// than rejecting. Mirror the server-side save check (createdCarePlanId) so the
// browser Accept path doesn't report success over a partially-applied write.
export function assertBatchSucceeded(result: Bundle): void {
  const failures = (result.entry ?? [])
    .map((entry) => entry.response?.status)
    .filter((status): status is string => {
      return Number.parseInt(status ?? '0', 10) >= 400;
    });
  if (failures.length > 0) {
    throw new Error(
      `Care plan save failed: ${failures.length} resource(s) rejected (${failures.join(', ')})`,
    );
  }
}
